// Simple Cognito service using direct URL redirects
// This avoids complex Amplify v6 auth imports that might be causing issues

class CognitoService {
  
  /**
   * Initiate login with specific identity provider
   * Used for domain-based routing to different SSO providers
   */
  static async loginWithProvider(identityProvider) {
    try {
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || 'pastely-enterprise-users.auth.us-east-1.amazoncognito.com';
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '69rm7tp9qjsrt1rsohga3ne5e8';
      const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://pastely.app/callback' 
          : 'http://localhost:3000/callback');

      // Direct URL to specific identity provider via Cognito
      const loginUrl = `https://${cognitoDomain}/oauth2/authorize?` +
        `identity_provider=${identityProvider}&` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `scope=email+openid+profile&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}`;

      // Redirect to Cognito hosted UI with specific identity provider
      window.location.href = loginUrl;
    } catch (error) {
      throw new Error('Failed to initiate SSO login');
    }
  }

  /**
   * Initiate Microsoft Enterprise login
   * Redirects user to Cognito hosted UI with Microsoft provider
   */
  static async loginWithMicrosoft() {
    try {
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || 'pastely-enterprise-users.auth.us-east-1.amazoncognito.com';
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '69rm7tp9qjsrt1rsohga3ne5e8';
      const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://pastely.app/callback' 
          : 'http://localhost:3000/callback');

      // Direct URL to Microsoft login via Cognito
      const loginUrl = `https://${cognitoDomain}/oauth2/authorize?` +
        `identity_provider=PasteAgentEntraID&` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `scope=email+openid+profile&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}`;

      // Redirect to Cognito hosted UI with Microsoft provider
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Microsoft login initiation failed:', error);
      throw new Error('Failed to initiate Microsoft login');
    }
  }

  /**
   * Handle the callback after successful authentication
   * This runs when user returns from Cognito
   */
  static async handleAuthCallback() {
    try {
      
      // Get authorization code from URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (!code) {
        throw new Error('No authorization code found in callback URL');
      }

      
      // Exchange code for tokens
      const tokenData = await this.exchangeCodeForTokens(code);
      
      if (!tokenData.id_token) {
        throw new Error('No ID token received from Cognito');
      }

      
      // Parse user info from ID token
      const userData = this.parseIdToken(tokenData.id_token);

      return userData;
      
    } catch (error) {
      
      // Re-throw with more context
      if (error.message.includes('Token exchange failed')) {
        throw new Error('Failed to process authentication callback: Token exchange failed');
      } else if (error.message.includes('No authorization code')) {
        throw new Error('Failed to process authentication callback: No authorization code');
      } else {
        throw new Error('Failed to process authentication callback: ' + error.message);
      }
    }
  }

  /**
   * Exchange authorization code for tokens
   */
  static async exchangeCodeForTokens(code) {
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || 'pastely-enterprise-users.auth.us-east-1.amazoncognito.com';
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '69rm7tp9qjsrt1rsohga3ne5e8';
    const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI || 
      (process.env.NODE_ENV === 'production' 
        ? 'https://pastely.app/callback' 
        : 'http://localhost:3000/callback');

    const tokenUrl = `https://${cognitoDomain}/oauth2/token`;
    
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code: code,
      redirect_uri: redirectUri
    });

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
      }

      const tokenData = await response.json();
      return tokenData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Parse user information from ID token
   */
  static parseIdToken(idToken) {
    try {
      // Simple JWT decode (just for the payload)
      const payload = idToken.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      
      return {
        userId: decoded.sub,
        email: decoded.email || 'unknown@example.com',
        name: decoded.name || decoded.email || 'Unknown User',
        picture: decoded.picture || null,
        provider: 'microsoft',
        givenName: decoded.given_name || 'Unknown',
        familyName: decoded.family_name || 'User',
        idToken: idToken
      };
    } catch (error) {
      throw new Error('Invalid ID token');
    }
  }

  /**
   * Sign out from Cognito
   */
  static async signOut() {
    try {
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || 'pastely-enterprise-users.auth.us-east-1.amazoncognito.com';
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '69rm7tp9qjsrt1rsohga3ne5e8';
      const logoutUri = process.env.NODE_ENV === 'production' 
        ? 'https://pastely.app/' 
        : 'http://localhost:3000/';

      const logoutUrl = `https://${cognitoDomain}/logout?` +
        `client_id=${clientId}&` +
        `logout_uri=${encodeURIComponent(logoutUri)}`;

      window.location.href = logoutUrl;
    } catch (error) {
      console.error('Cognito sign out failed:', error);
    }
  }

  /**
   * Get stored access token
   */
  static async getAccessToken() {
    try {
      // For now, return the stored token from localStorage
      // In a production app, you'd want to handle token refresh
      return localStorage.getItem('pastelyToken');
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user is authenticated with Cognito
   */
  static async isAuthenticated() {
    try {
      const user = localStorage.getItem('pastelyUser');
      if (user) {
        const parsedUser = JSON.parse(user);
        return parsedUser.provider === 'microsoft';
      }
      return false;
    } catch (error) {
      return false;
    }
  }
}

export default CognitoService;
